import { useCallback, useEffect, useState } from "react";
import {
  Bot, CheckCircle2, Clock, Inbox, Loader2, Mail, MailQuestion,
  Paperclip, Pause, Play, RefreshCw, Sparkles, TriangleAlert, Users, Banknote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adminApi } from "@/lib/auth";
import { MailboxesPanel } from "@/components/MailboxesPanel";
import { toast } from "sonner";

const STATUS = {
  created: { label: "CURSO CREADO", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: CheckCircle2 },
  updated: { label: "CURSO ACTUALIZADO", cls: "bg-sky-100 text-sky-800 border-sky-300", Icon: RefreshCw },
  unchanged: { label: "SIN CAMBIOS", cls: "bg-zinc-100 text-zinc-700 border-zinc-300", Icon: CheckCircle2 },
  pending: { label: "FALTAN DATOS", cls: "bg-amber-100 text-amber-900 border-amber-300", Icon: MailQuestion },
  ignored: { label: "NO ES UN CURSO", cls: "bg-zinc-100 text-zinc-500 border-zinc-200", Icon: Mail },
  error: { label: "ERROR", cls: "bg-red-100 text-red-800 border-red-300", Icon: TriangleAlert },
};

const STATUS_FILTERS = [
  ["created", "Creados", "text-emerald-700"],
  ["updated", "Actualizados", "text-sky-700"],
  ["unchanged", "Sin cambios", "text-zinc-600"],
  ["pending", "Faltan datos", "text-amber-700"],
  ["ignored", "No eran cursos", "text-zinc-500"],
  ["error", "Errores", "text-red-700"],
];

const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const Chip = ({ label, value }) =>
  value === null || value === undefined || value === "" ? null : (
    <span className="inline-flex items-center gap-1 rounded-sm border border-zinc-200 bg-white px-2 py-0.5 text-[11px]">
      <span className="font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
      <span className="text-zinc-900">{value}</span>
    </span>
  );

const fmtEur = (n) =>
  `${(Number(n) || 0)
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".")} €`;

const RemittanceCard = ({ rem, onDismiss }) => {
  const results = rem.results || [];
  const marked = results.filter((r) => r.status === "pagada").length;
  const already = results.filter((r) => r.status === "ya_pagada").length;
  const unpaid = results.filter((r) => r.status !== "pagada" && r.status !== "ya_pagada");
  const bad = rem.status === "error" || unpaid.length > 0;
  return (
    <article
      className={`rounded-sm border px-3 py-2 ${bad ? "border-red-300 bg-red-50" : "border-zinc-200 bg-white"}`}
      data-testid={`reception-remittance-${rem.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-bold text-zinc-900">{rem.subject}</p>
          <p className="text-xs text-zinc-500">{rem.from} · {fmtDate(rem.received)}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => onDismiss(rem.id)}
          className="h-8 border-2 border-zinc-300 text-xs font-semibold"
          data-testid={`reception-remittance-reviewed-${rem.id}`}>
          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
          Visto
        </Button>
      </div>
      {rem.status === "error" ? (
        <p className="mt-2 text-sm font-semibold text-red-800">{rem.detail}</p>
      ) : (
        <p className="mt-2 text-sm font-semibold text-zinc-900" data-testid={`reception-remittance-summary-${rem.id}`}>
          <span className="text-emerald-800">{marked} factura(s) marcadas como pagadas</span>
          {already > 0 && <span className="text-zinc-500"> · {already} ya estaban cobradas</span>}
          {unpaid.length > 0 && <span className="text-red-800"> · {unpaid.length} sin marcar</span>}
        </p>
      )}
      {unpaid.length > 0 && (
        <table className="mt-2 w-full text-xs" data-testid={`reception-remittance-unpaid-${rem.id}`}>
          <tbody>
            {unpaid.map((r, i) => (
              <tr key={`${r.number}-${i}`} className="border-t border-red-100">
                <td className="py-1 font-mono">{r.invoice_number || r.number}</td>
                <td className="py-1 text-right tabular-nums">{fmtEur(r.amount)}</td>
                <td className="py-1 pl-3 font-semibold text-red-800">{r.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </article>
  );
};

const RemittancesPanel = ({ reloadKey, paused }) => {
  const [data, setData] = useState({ remittances: [], configured: false });
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/reception/remittances");
      setData(res.data);
    } catch (e) {
      // el panel es secundario: si falla, no bloqueamos la bandeja
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await adminApi.post("/admin/reception/remittances/run");
      if (res.data?.error) toast.error(res.data.error);
      else if (res.data?.remittances)
        toast.success(
          `${res.data.remittances} remesa(s) · ${res.data.marked_paid} factura(s) pagadas`
        );
      else toast.info("No ha entrado ninguna remesa nueva.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron comprobar las remesas.");
    } finally {
      setRunning(false);
    }
  };

  const dismiss = async (id) => {
    try {
      await adminApi.post(`/admin/reception/remittances/${id}/reviewed`);
      setData((d) => ({ ...d, remittances: (d.remittances || []).filter((r) => r.id !== id) }));
    } catch (e) {
      toast.error("No se pudo quitar la remesa.");
    }
  };

  return (
    <section
      className="rounded-sm border-2 border-zinc-200 bg-white p-4"
      data-testid="reception-remittances-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-extrabold text-zinc-950">
            <Banknote className="h-5 w-5 text-emerald-700" />
            Remesas de BSAFE
          </h3>
          <p className="mt-1 text-sm text-zinc-600">
            La remesa mensual llega desde{" "}
            <span className="font-mono font-semibold text-zinc-900" data-testid="reception-remittances-sender">
              {(data.senders || []).join(", ") || "irivas@almas-industries.com"}
            </span>{" "}
            a uno de los buzones de pedidos. Se marcan como cobradas las
            facturas que cuadran; aquí solo aparecen las que no se han podido
            marcar. Pulsa «Visto» para quitar la remesa de la lista.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={runNow}
          disabled={running || !data.configured || paused}
          className="h-10 border-2 border-zinc-300 font-semibold"
          data-testid="reception-remittances-run-btn"
        >
          {running ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Comprobando…</>
          ) : (
            <><RefreshCw className="mr-2 h-4 w-4" />Comprobar remesas ahora</>
          )}
        </Button>
      </div>

      {(data.remittances || []).length > 0 && (
        <div className="mt-4 space-y-3">
          {data.remittances.map((rem) => (
            <RemittanceCard key={rem.id} rem={rem} onDismiss={dismiss} />
          ))}
        </div>
      )}
    </section>
  );
};

export const ReceptionManager = ({ onUnreviewedChange }) => {
  const [data, setData] = useState({ emails: [], unreviewed: 0, configured: false });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState({ subject: "", body: "" });
  const [testFiles, setTestFiles] = useState([]);
  const [testing, setTesting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("created");
  const [pausing, setPausing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/reception", { params: { status: statusFilter } });
      setData(res.data);
      onUnreviewedChange?.(res.data.unreviewed || 0);
    } catch (e) {
      toast.error("No se pudo cargar la bandeja del recepcionista.");
    } finally {
      setLoading(false);
    }
  }, [onUnreviewedChange, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const togglePause = async () => {
    setPausing(true);
    try {
      const res = await adminApi.post("/admin/reception/pause", { paused: !data.paused });
      toast.success(res.data.paused
        ? "Lectura de buzones en pausa."
        : "Lectura reanudada: se leerán también los correos llegados durante la pausa.");
      await load();
      setMailboxesKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar la pausa.");
    } finally {
      setPausing(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await adminApi.post("/admin/reception/run");
      const s = res.data || {};
      if (s.running) {
        toast.info("Hay muchos correos: se siguen leyendo en segundo plano. Vuelve a abrir Recepción en un par de minutos.");
      } else if (s.error) toast.error(s.error);
      else if (!s.fetched) toast.info("No había correos nuevos en el buzón.");
      else {
        toast.success(
          `${s.fetched} correo(s): ${s.created} curso(s) creado(s), ${s.updated} actualizado(s)` +
          (s.pending ? `, ${s.pending} con datos incompletos` : "")
        );
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo leer el buzón.");
    } finally {
      setRunning(false);
    }
  };

  const runTest = async () => {
    if (!testForm.body.trim() && testFiles.length === 0) {
      toast.error("Pega el texto del correo o adjunta un fichero.");
      return;
    }
    setTesting(true);
    try {
      const fd = new FormData();
      fd.append("subject", testForm.subject);
      fd.append("body", testForm.body);
      testFiles.forEach((f) => fd.append("files", f));
      const res = await adminApi.post("/admin/reception/simulate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data?.detail || "Correo analizado.");
      setTestOpen(false);
      setTestForm({ subject: "", body: "" });
      setTestFiles([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo analizar el correo.");
    } finally {
      setTesting(false);
    }
  };

  const markReviewed = async (id) => {
    try {
      await adminApi.post(`/admin/reception/${id}/reviewed`);
      await load();
    } catch (e) {
      toast.error("No se pudo quitar el correo.");
    }
  };

  const [mailboxesKey, setMailboxesKey] = useState(0);
  const onMailboxesChange = useCallback(() => {
    setMailboxesKey((k) => k + 1);
    load();
  }, [load]);

  const counts = data.counts || {};

  return (
    <section className="space-y-6" data-testid="reception-manager">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-zinc-950">
            Agente Recepcionista
          </h2>
          <p className="mt-1 text-sm text-zinc-600">
            Lee{" "}
            <span className="font-mono font-semibold text-zinc-900">
              {data.mailbox || "los buzones de pedidos"}
            </span>{" "}
            cada 15 minutos, entiende el correo (y sus adjuntos PDF o Excel)
            con IA, da de alta o actualiza el curso y pre-registra a los
            alumnos con nombre, apellidos y DNI. El instructor queda como{" "}
            <b>PENDIENTE BUSCAR INSTRUCTOR</b>. Al pulsar «Visto» el correo
            desaparece de la bandeja.
          </p>
          {data.last_run && (
            <p className="mt-1 text-xs text-zinc-500" data-testid="reception-last-run">
              Última revisión: {fmtDate(data.last_run.at)} ({data.last_run.trigger})
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={togglePause}
            disabled={pausing || loading}
            className={`h-10 border-2 font-semibold ${
              data.paused
                ? "border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                : "border-amber-500 text-amber-800 hover:bg-amber-50"
            }`}
            data-testid="reception-pause-button"
          >
            {data.paused ? (
              <><Play className="mr-2 h-4 w-4" />Reanudar lectura</>
            ) : (
              <><Pause className="mr-2 h-4 w-4" />Pausar lectura</>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setTestOpen(true)}
            className="h-10 border-2 border-zinc-300 font-semibold"
            data-testid="reception-test-button"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Probar con un correo
          </Button>
          <Button
            onClick={runNow}
            disabled={running || !data.configured || data.paused}
            className="h-10 bg-emerald-600 font-semibold hover:bg-emerald-700"
            data-testid="reception-run-button"
          >
            {running ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Leyendo buzón…</>
            ) : (
              <><Inbox className="mr-2 h-4 w-4" />Revisar buzón ahora</>
            )}
          </Button>
        </div>
      </div>

      {data.paused && (
        <div className="flex items-center gap-2 rounded-sm border-2 border-amber-400 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900"
          data-testid="reception-paused-banner">
          <Pause className="h-4 w-4 shrink-0" />
          Lectura de buzones en pausa{data.paused_at ? ` desde ${fmtDate(data.paused_at)}` : ""}: no se leen
          pedidos ni remesas. Al reanudar se leerán los correos que lleguen mientras tanto.
        </div>
      )}

      {!data.configured && !loading && (
        <div className="rounded-sm border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900"
          data-testid="reception-not-configured">
          No hay ningún buzón de pedidos: añádelo en «Buzones de pedidos de cursos».
        </div>
      )}

      <MailboxesPanel onChange={onMailboxesChange} />

      <RemittancesPanel reloadKey={mailboxesKey} paused={data.paused} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="reception-status-filters">
        {STATUS_FILTERS.map(([key, label, cls]) => {
          const active = statusFilter === key;
          return (
            <button
              type="button"
              key={key}
              onClick={() => setStatusFilter(key)}
              aria-pressed={active}
              className={`rounded-sm border-2 bg-white px-3 py-2 text-left transition-colors hover:border-zinc-400 ${
                active ? "border-zinc-950 ring-2 ring-zinc-950/10" : "border-zinc-200"
              }`}
              data-testid={`reception-filter-${key}`}
            >
              <div className={`text-xl font-extrabold tabular-nums ${cls}`}>{counts[key] || 0}</div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                {label}
              </div>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando bandeja…
        </div>
      ) : (data.emails || []).length === 0 ? (
        <div className="rounded-sm border-2 border-dashed border-zinc-300 bg-white px-6 py-12 text-center"
          data-testid="reception-empty">
          <Bot className="mx-auto h-8 w-8 text-zinc-400" />
          <p className="mt-3 font-bold text-zinc-900">No hay correos en este apartado</p>
          <p className="mt-1 text-sm text-zinc-500">
            Pulsa otro apartado de arriba para ver sus correos. El agente
            revisa el buzón cada 15 minutos.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {(data.emails || []).map((mail) => {
            const meta = STATUS[mail.status] || STATUS.error;
            const ex = mail.extracted || {};
            const highlight =
              mail.status === "pending" || mail.status === "error";
            return (
              <article
                key={mail.id}
                data-testid={`reception-email-${mail.id}`}
                className={`rounded-sm border-2 bg-white px-4 py-3 ${
                  highlight ? "border-amber-300" : "border-zinc-200"
                } ${mail.reviewed ? "opacity-70" : ""}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        className={`rounded-sm border px-2 py-0.5 text-[10px] font-bold ${meta.cls}`}
                        data-testid={`reception-email-status-${mail.id}`}
                      >
                        {meta.label}
                      </Badge>
                      <span className="text-xs text-zinc-500">
                        <Clock className="mr-1 inline h-3 w-3" />
                        {fmtDate(mail.processed_at)}
                      </span>
                      {mail.trigger === "pegado" && (
                        <span className="text-[10px] font-semibold uppercase text-zinc-400">
                          pegado a mano
                        </span>
                      )}
                    </div>
                    <h3 className="mt-1 truncate font-bold text-zinc-950">
                      {mail.subject || "(sin asunto)"}
                    </h3>
                    <p className="truncate text-xs text-zinc-500">{mail.from}</p>
                  </div>
                  {!mail.reviewed && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markReviewed(mail.id)}
                      className="h-8 border-2 border-zinc-300 text-xs font-semibold"
                      data-testid={`reception-reviewed-${mail.id}`}
                    >
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                      Visto
                    </Button>
                  )}
                </div>

                {(mail.attachments || []).length > 0 && (
                  <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-zinc-600">
                    <Paperclip className="h-3.5 w-3.5 text-zinc-400" />
                    {(mail.attachments || []).map((f) => (
                      <span
                        key={f}
                        className="rounded-sm border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[11px]"
                      >
                        {f}
                      </span>
                    ))}
                  </p>
                )}
                {mail.students_imported > 0 && (
                  <p
                    className="mt-2 inline-flex items-center gap-1.5 rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-800"
                    data-testid={`reception-students-${mail.id}`}
                  >
                    <Users className="h-3.5 w-3.5" />
                    {mail.students_imported} alumno(s) pre-registrados
                    {(mail.students_notes || []).length > 0 && (
                      <span className="font-normal text-emerald-700">
                        · {(mail.students_notes || []).join(" · ")}
                      </span>
                    )}
                  </p>
                )}
                {mail.detail && (
                  <p className="mt-2 text-sm font-semibold text-zinc-800">
                    {mail.detail}
                  </p>
                )}
                {mail.summary && (
                  <p className="mt-1 text-xs italic text-zinc-500">{mail.summary}</p>
                )}

                {ex.es_curso && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Chip label="Cliente" value={ex.cliente} />
                    <Chip label="Dirección" value={ex.direccion} />
                    <Chip label="CP" value={ex.codigo_postal} />
                    <Chip label="Localidad" value={ex.localidad} />
                    <Chip label="Provincia" value={ex.provincia} />
                    <Chip
                      label="Tipo"
                      value={ex.tipo_curso ? ex.tipo_curso.toUpperCase() : null}
                    />
                    <Chip label="Fechas" value={(ex.fechas || []).join(" · ")} />
                    <Chip
                      label="Horario"
                      value={(ex.horarios || [])
                        .filter((hh) => hh.inicio)
                        .map((hh) => `${hh.inicio}-${hh.fin || "?"}`)
                        .join(" · ")}
                    />
                    <Chip label="DEA" value={ex.modelo_desfibrilador} />
                    <Chip
                      label="FUNDAE"
                      value={ex.fundae === null ? null : ex.fundae ? "SÍ" : "NO"}
                    />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-2xl" data-testid="reception-test-dialog">
          <DialogHeader>
            <DialogTitle>Probar el recepcionista</DialogTitle>
            <DialogDescription>
              Pega el texto de un correo y el agente hará exactamente lo mismo
              que con los del buzón: extraer los datos y crear o actualizar el
              curso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-600">
                Asunto
              </label>
              <Input
                value={testForm.subject}
                onChange={(e) => setTestForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="Reciclaje DESA — 20 de noviembre"
                className="h-11 border-2 border-zinc-300"
                data-testid="reception-test-subject"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-600">
                Cuerpo del correo
              </label>
              <Textarea
                value={testForm.body}
                onChange={(e) => setTestForm((f) => ({ ...f, body: e.target.value }))}
                rows={10}
                placeholder="Pega aquí el correo del cliente…"
                className="border-2 border-zinc-300 font-mono text-xs"
                data-testid="reception-test-body"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-600">
                Adjuntos (PDF, Excel o CSV)
              </label>
              <input
                type="file"
                multiple
                accept=".pdf,.xlsx,.xlsm,.csv,.txt"
                onChange={(e) => setTestFiles(Array.from(e.target.files || []))}
                className="block w-full cursor-pointer rounded-sm border-2 border-zinc-300 p-2 text-xs file:mr-3 file:rounded-sm file:border-0 file:bg-zinc-950 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white"
                data-testid="reception-test-files"
              />
              {testFiles.length > 0 && (
                <p className="mt-1 text-[11px] text-zinc-500">
                  {testFiles.map((f) => f.name).join(" · ")}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestOpen(false)}
              className="h-10 border-2 border-zinc-300 font-semibold"
            >
              Cancelar
            </Button>
            <Button
              onClick={runTest}
              disabled={testing}
              className="h-10 bg-zinc-950 font-semibold hover:bg-zinc-800"
              data-testid="reception-test-submit"
            >
              {testing ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analizando…</>
              ) : (
                <><Bot className="mr-2 h-4 w-4" />Analizar correo</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ReceptionManager;
